import { useForm } from 'react-hook-form';
import {
  useDebounce,
  useModalStore,
  useWithdrawOperationData,
} from '@/shared/lib';
import {
  type CardCheckCreateSuccessResponse,
  createWithdrawCardFormSchema,
  type CurrencyAssetOption,
  useCheckAndCreateCard,
  useWithdrawCommission,
  type WithdrawAmountValues,
  type WithdrawCardFormValues,
} from '@/features/withdraw-card';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { COMMISSION_CHECK_DELAY } from '@/shared/config';
import {
  type FiatWithdrawAssetOption,
  getFiatWithdrawAssetOptions,
  useWithdrawTotalAmount,
} from '@/entities/operation';
import { useCardHolderName, useGetSavedCards } from '@/entities/card';
import { useUserProfileInfo } from '@/entities/user';
import type { CommissionResponse } from '@/shared/types';

interface UseWithdrawCardFormProps {
  onValidateSuccess: (userCardId: string) => void;
}

export function useWithdrawCardForm({
  onValidateSuccess,
}: UseWithdrawCardFormProps) {
  const { t } = useTranslation();
  const { getState } = useModalStore();

  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyAssetOption | undefined
  >();
  const [templateId, setTemplateId] = useState<string | undefined>();
  const [selectedIncludeFee, setSelectedIncludeFee] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<
    CommissionResponse | undefined
  >(undefined);
  const [totalAmount, setTotalAmount] = useState(0);

  const { userProfileInfoMap } = useUserProfileInfo();
  const { cardHolderName } = useCardHolderName({
    firstName: userProfileInfoMap.firstName,
    lastName: userProfileInfoMap.lastName,
  });

  const {
    ewallet,
    depositWithdrawAssetsLoading,
    depositWithdrawAssetsError,
    depositWithdrawAssetsIsError,
    depositWithdrawAssetsData,
    assetsIconsData,
    assetsData,
    mainAccount,
  } = useWithdrawOperationData();

  const {
    data: savedCards,
    isLoading: isSavedCardsLoading,
    error: savedCardsError,
  } = useGetSavedCards();

  const cardIconConfig: Record<string, string> = {};

  if (
    Array.isArray(depositWithdrawAssetsData?.value?.withdraw?.paymentSystem)
  ) {
    const paymentSysKey = 'cards';
    const cardsPaymentSys =
      depositWithdrawAssetsData.value.withdraw.paymentSystem.find(item =>
        Object.keys(item).includes(paymentSysKey)
      );

    if (cardsPaymentSys && Array.isArray(cardsPaymentSys[paymentSysKey])) {
      cardsPaymentSys[paymentSysKey].forEach(card => {
        if (!card.icon) {
          return;
        }

        (card.startNum || []).forEach(startNum => {
          cardIconConfig[startNum] = card.icon;
        });
      });
    }
  }

  const assetOptions = useMemo<FiatWithdrawAssetOption[]>(() => {
    return getFiatWithdrawAssetOptions({
      key: 'card',
      assetsData,
      assetsIconsData,
      depositWithdrawAssetsData,
      mainAccount,
      ewallet,
    });
  }, [
    assetsData,
    assetsIconsData,
    ewallet,
    depositWithdrawAssetsData,
    mainAccount,
  ]);

  const initFormData = getState('withdrawCardFormData') as
    | WithdrawCardFormValues
    | undefined;

  const validationSchema = useMemo(() => {
    const amount = selectedIncludeFee
      ? totalAmount
      : totalAmount - (selectedCommission?.feeAmount ?? 0);

    return createWithdrawCardFormSchema(
      selectedCurrency?.min,
      selectedCurrency?.available,
      amount,
      selectedCurrency?.max,
      t('common.maxAmount', {
        amount: selectedCurrency?.max,
        currency: selectedCurrency?.code ?? '',
      })
    );
  }, [
    t,
    selectedCurrency?.max,
    selectedCommission?.feeAmount,
    selectedIncludeFee,
    selectedCurrency?.available,
    selectedCurrency?.code,
    selectedCurrency?.min,
    totalAmount,
  ]);

  const form = useForm<WithdrawCardFormValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      currencyCode: initFormData?.currencyCode || '',
      cardNumber: initFormData?.cardNumber || '',
      cardholderName: initFormData?.cardholderName || cardHolderName,
      isCardholderCard: initFormData?.isCardholderCard || false,
      amount: initFormData?.amount ?? '',
      includeFee: initFormData?.includeFee || false,
      userCardId: initFormData?.userCardId || '',
    },
  });

  const watchedValues = form.watch();

  useEffect(() => {
    const currencyItem = assetOptions.find(
      a =>
        a.code === watchedValues.currencyCode ||
        a.code === initFormData?.currencyCode
    );

    if (currencyItem) {
      setSelectedCurrency(currencyItem);
    }
  }, [watchedValues.currencyCode, initFormData]);

  useEffect(() => {
    setSelectedIncludeFee(watchedValues.includeFee ?? false);
  }, [watchedValues.includeFee]);

  const debouncedAmount = useDebounce(
    watchedValues.amount,
    COMMISSION_CHECK_DELAY
  );
  const amountError = form.formState.errors.amount;
  const hasAmountError = !!amountError;

  const commissionEnabled = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    const isTyping = watchedValues.amount !== debouncedAmount;
    if (isTyping) {
      return false;
    }

    const amount = Number(debouncedAmount);

    if (selectedCurrency?.min !== undefined && selectedCurrency?.min > amount) {
      return false;
    }

    if (
      selectedCurrency?.available !== undefined &&
      amount > selectedCurrency?.available
    ) {
      return false;
    }

    const hasValidDebouncedAmount = !!debouncedAmount;
    const hasCurrency = !!selectedCurrency?.code;

    return hasValidDebouncedAmount && hasCurrency;
  }, [
    debouncedAmount,
    selectedCurrency?.code,
    hasAmountError,
    watchedValues.amount,
    watchedValues.includeFee,
  ]);

  const { commissionData, commissionError, isCommissionFetching } =
    useWithdrawCommission({
      currencyCode: selectedCurrency?.code || '',
      amount: debouncedAmount,
      onTop: watchedValues.includeFee,
      enabled: commissionEnabled,
    });

  useEffect(() => {
    setSelectedCommission(commissionData);
  }, [commissionData, isCommissionFetching]);

  const { checkAndCreateCard, checkAndCreateCardError } =
    useCheckAndCreateCard();

  const [amount, includeFee] = form.watch(['amount', 'includeFee']);

  const amountValues: WithdrawAmountValues = {
    amount: amount,
    includeFee: includeFee ?? false,
  };
  const { totalAmount: calculatedTotalAmount } = useWithdrawTotalAmount<
    WithdrawCardFormValues,
    WithdrawAmountValues
  >({
    form,
    watchedValues: amountValues,
    commissionData,
    assetCode: selectedCurrency?.code,
    isCommissionLoading: isCommissionFetching,
    additionalDependencies: [selectedCurrency?.code],
    validationDependencies: [
      amountValues.amount,
      selectedCurrency?.code,
      totalAmount,
    ],
  });

  useEffect(() => {
    setTotalAmount(calculatedTotalAmount);
  }, [calculatedTotalAmount]);

  const isGettingCommission = useMemo(() => {
    if (hasAmountError) {
      return false;
    }

    const hasAmount = !!watchedValues.amount;
    const hasCurrency = !!selectedCurrency?.code;
    const isTyping = watchedValues.amount !== debouncedAmount;

    return (isTyping && hasAmount && hasCurrency) || isCommissionFetching;
  }, [
    watchedValues.amount,
    debouncedAmount,
    selectedCurrency?.code,
    hasAmountError,
    isCommissionFetching,
  ]);

  const handleMaxClick = () => {
    if (selectedCurrency) {
      form.setValue('amount', String(selectedCurrency.available));
    }
  };

  const handleFormSubmit = async () => {
    const data = form.getValues();

    if (data.userCardId) {
      onValidateSuccess(data.userCardId);
      return;
    }

    const result = (await checkAndCreateCard({
      cardNumber: data.cardNumber,
      cardholderName: data.cardholderName,
      isCardholderCard: data.isCardholderCard,
      currencyCode: data.currencyCode,
    })) as CardCheckCreateSuccessResponse;

    onValidateSuccess(result.userCardId);
  };

  return {
    form,
    watchedValues,
    assetOptions,
    savedCards,
    selectedCurrency,
    totalAmount,
    templateId,
    setTemplateId,
    hasWithdrawFee: commissionData ? commissionData.feeAmount > 0 : false,
    isFromDataLoading: depositWithdrawAssetsLoading || isSavedCardsLoading,
    isFormDataError: depositWithdrawAssetsIsError,
    formDataError: depositWithdrawAssetsError || savedCardsError,
    commissionData,
    handleFormSubmit,
    error: commissionError || checkAndCreateCardError,
    handleMaxClick,
    isLoading: isGettingCommission,
    accountNumber: mainAccount?.accNum,
    cardIconConfig,
  };
}
